<template>
  <div>
    <div class="iq-filters">
      <div class="iq-filters__left">
        <button
          type="button"
          class="iq-filterbtn"
          :class="{ 'iq-filterbtn--on': filtersOpen }"
          @click="filtersOpen = !filtersOpen"
          >
          <hero-icon :icon="FilterIcon" :size="17" />
          <span>Filtros</span>
          <span v-if="activeCount" class="iq-filterbtn__badge">{{ activeCount }}</span>
        </button>

        <!-- sort is always visible (not a hidden filter): the segmented control
             itself is the cue for how the list is currently ordered -->
        <div class="iq-sort" role="group" aria-label="Ordenar por">
          <span class="iq-sort__icon" title="Orden del listado (descendente)">
            <hero-icon :icon="SortIcon" :size="15" />
          </span>
          <button
            v-for="o in orderList"
            :key="o.value"
            type="button"
            class="iq-sort__opt"
            :class="{ 'iq-sort__opt--on': order === o.value }"
            :title="o.hint"
            @click="order = o.value"
            >
            {{ o.text }}
          </button>
        </div>
      </div>

      <create @reload="fetchInquiries" :owner="owner"></create>
    </div>

    <div v-if="filtersOpen" class="iq-filterpanel">
      <div class="iq-filterpanel__label">Filtros</div>
      <div class="iq-chips">
        <button
          type="button"
          class="iq-chip"
          :class="{ 'iq-chip--on': mine }"
          @click="mine = !mine"
          >
          Asignadas a mí
        </button>

        <button
          v-if="!$route.params.id"
          type="button"
          class="iq-chip"
          :class="{ 'iq-chip--on': manual }"
          @click="manual = !manual"
          >
          Creadas manualmente
        </button>
      </div>

      <template v-if="!type && !owner && !$route.params.id">
        <div class="iq-filterpanel__label">Operación</div>
        <div class="iq-chips">
          <button type="button" class="iq-chip" :class="{ 'iq-chip--on': operationType === 'sell' }" @click="toggleOperation('sell')">Venta</button>
          <button type="button" class="iq-chip" :class="{ 'iq-chip--on': operationType === 'rent' }" @click="toggleOperation('rent')">Alquiler</button>
        </div>
      </template>

      <div class="iq-filterpanel__label">Estado</div>

      <div class="iq-chips">
        <button
          v-for="s in statusList"
          :key="s.value"
          type="button"
          class="iq-chip"
          :class="{ 'iq-chip--on': status.includes(s.value) }"
          @click="toggleStatus(s.value)"
          >
          {{ s.text }}
        </button>
      </div>
    </div>

    <card
      v-for="inquiry in inquiries"
      :key="inquiry.id"
      :inquiry="inquiry"
      :order="order"
      :show-journey="true"
      @reload="fetchInquiries"
      @open-property="openProperty"
      @open-journey="openJourney"
      ></card>

    <div v-if="!inquiries.length && !loading" class="iq-empty">
      No hay consultas para mostrar.
    </div>

    <div v-if="loading" class="iq-loading">Cargando…</div>

    <div ref="sentinel" class="iq-sentinel"></div>

    <property-detail ref="propDetail" @reload="fetchInquiries"></property-detail>
    <inquiry-detail-dialog ref="inquiryDetail" @reload="fetchInquiries"></inquiry-detail-dialog>
    <visitor-journey-dialog
      ref="journey"
      @open-property="openProperty"
      @open-inquiry="openInquiry"
    ></visitor-journey-dialog>
  </div>
</template>

<script>
import Card from "@/components/inquiries/Card";
import Create from "@/components/inquiries/Create";
import PropertyDetail from "@/components/properties/Detail";
import VisitorJourneyDialog from "@/components/reports/VisitorJourneyDialog";
import InquiryDetailDialog from "@/components/inquiries/InquiryDetailDialog";
import HeroIcon from "@/components/shared/HeroIcon";
import FilterIcon from "heroicons/24/outline/funnel.svg";
import SortIcon from "heroicons/24/outline/bars-arrow-down.svg";

import { Inquiries } from "@/graphql/queries/inquiries";
import { subscribeInquiries } from "@/services/inquiriesCable";
import { FUNNEL } from "@/utils/funnel";

export default {
  data: () => ({
    FilterIcon,
    SortIcon,
    filtersOpen: false,
    inquiries: [],
    status: [], // seeded from initialStatus in created()
    // the funnel stages (see utils/funnel.js)
    statusList: FUNNEL.map(s => ({ text: s.label, value: s.state })),
    mine: false,
    manual: false,
    operationType: null,
    order: "latest_activity",
    orderList: [
      { text: "Actividad", value: "latest_activity", hint: "Última actividad primero — una consulta con actividad nueva vuelve arriba" },
      { text: "Recientes", value: "created_at", hint: "Fecha de creación, más nuevas primero" },
      { text: "No leídas", value: "unread", hint: "Sin leer primero, leídas después — más nuevas primero en cada grupo" },
      { text: "Temperatura", value: "temperature", hint: "Temperatura del lead — calientes primero" }
    ],
    pages: 0,
    page: 1,
    perPage: 15,
    loading: false,
    observer: null
  }),

  props: {
    type: {
      type: String,
      required: false,
      default: null
    },

    owner: {
      type: Object,
      required: false,
      default: null
    },

    // pre-filter to one funnel stage (the board's "+N más" hands off here)
    initialStatus: {
      type: String,
      required: false,
      default: null
    }
  },

  computed: {
    activeCount() {
      return (this.mine ? 1 : 0) + (this.manual ? 1 : 0) + (this.operationType ? 1 : 0) + this.status.length;
    }
  },

  watch: {
    mine() {
      this.fetchInquiries();
    },
    manual() {
      this.fetchInquiries();
    },
    operationType() {
      this.fetchInquiries();
    },
    status() {
      this.fetchInquiries();
    },
    order() {
      this.fetchInquiries();
    }
  },

  created() {
    // live: refresh in place while the agent is at the top; once they've paged
    // deeper, leave their scroll alone (the bell still signals the new activity)
    this._sub = subscribeInquiries(e => {
      if (!["inquiryCreated", "inquiryUpdated", "inquiryDeleted", "comment", "state"].includes(e.type)) return;
      if (this.page === 1 && !this.loading) this.fetchInquiries();
    });

    if (this.initialStatus) {
      this.status = [this.initialStatus]; // the status watcher fires the fetch
      this.filtersOpen = true; // make the inherited filter visible/undoable
      return;
    }
    this.fetchInquiries();
  },

  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) this.loadMore();
      },
      { rootMargin: "400px" }
    );
    if (this.$refs.sentinel) this.observer.observe(this.$refs.sentinel);
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    if (this._sub) this._sub.unsubscribe();
  },

  methods: {
    // entry point (initial load, filter change, post-mutation @reload):
    // always restart from page 1 and replace the list
    fetchInquiries() {
      this.page = 1;
      this.fetchPage(false);
    },

    fetchPage(append) {
      this.loading = true;

      this.$apollo
        .query({
          query: Inquiries,
          variables: {
            type: this.type,
            page: this.page,
            perPage: this.perPage,
            ownerId: (this.owner && this.owner.id) || this.$route.params.id,
            mine: this.mine,
            manual: this.manual,
            status: this.status,
            operationType: this.operationType,
            order: this.order
          },
          fetchPolicy: "no-cache"
        })
        .then(res => {
          const list = res.data.inquiries.inquiries;
          this.inquiries = append ? this.inquiries.concat(list) : list;
          this.pages = res.data.inquiries.pages;
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(this.maybeLoadMore);
        });
    },

    loadMore() {
      if (this.loading || this.page >= this.pages) return;
      this.page += 1;
      this.fetchPage(true);
    },

    // after a (re)load, if the sentinel is already on-screen keep filling
    maybeLoadMore() {
      const el = this.$refs.sentinel;
      if (el && el.getBoundingClientRect().top <= window.innerHeight + 400) {
        this.loadMore();
      }
    },

    toggleStatus(value) {
      const i = this.status.indexOf(value);
      if (i >= 0) this.status.splice(i, 1);
      else this.status.push(value);
    },

    toggleOperation(value) {
      this.operationType = this.operationType === value ? null : value;
    },

    openProperty(property) {
      this.$refs.propDetail.open(property);
    },

    openJourney(visitorId) {
      this.$refs.journey.open(visitorId);
    },

    openInquiry(inquiryId) {
      this.$refs.inquiryDetail.open(inquiryId);
    }
  },

  components: {
    Card,
    Create,
    PropertyDetail,
    VisitorJourneyDialog,
    InquiryDetailDialog,
    HeroIcon
  }
};
</script>

<style scoped>
.iq-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.iq-filters__left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* always-visible sort segmented control */
.iq-sort {
  display: flex;
  align-items: center;
  /* four options no longer fit one phone-width row — wrap inside the pill box
     instead of overflowing it */
  flex-wrap: wrap;
  gap: 2px;
  background: #fff;
  border: 1px solid #e7e2d9;
  border-radius: 10px;
  padding: 3px;
}
.iq-sort__icon {
  display: flex;
  align-items: center;
  color: #a39a8c;
  padding: 0 4px 0 8px;
}
.iq-sort__opt {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: #56504a;
  padding: 6px 11px;
  border-radius: 7px;
  transition: all 0.15s ease;
}
.iq-sort__opt:hover { color: #1a1814; }
.iq-sort__opt--on { background: #1a1814; color: #fff; }

/* "Filtros" toggle button (mirrors the public page's filter button) */
.iq-filterbtn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e7e2d9;
  border-radius: 10px;
  padding: 9px 16px;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1814;
  transition: border-color 0.15s ease;
}
.iq-filterbtn:hover,
.iq-filterbtn--on { border-color: #cfc7b8; }
.iq-filterbtn .hero-icon { color: #6a645a; }
.iq-filterbtn__badge {
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #b52118;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

.iq-filterpanel {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
}
.iq-filterpanel__label {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
  margin: 14px 0 9px;
}

/* chips (mirror the public page's type chips) */
.iq-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.iq-chip {
  border-radius: 999px;
  border: 1px solid #e3ddd2;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  font-size: 12.5px;
  color: #56504a;
  padding: 8px 14px;
  transition: all 0.15s ease;
}
.iq-chip:hover { border-color: #cfc7b8; }
.iq-chip--on { background: #1a1814; color: #fff; border-color: #1a1814; }
.iq-loading {
  text-align: center;
  color: #a39a8c;
  font-size: 13px;
  padding: 14px 0 4px;
}
.iq-sentinel {
  height: 1px;
}
.iq-empty {
  border: 1px dashed #dcd4c6;
  border-radius: 12px;
  padding: 36px;
  text-align: center;
  color: #a39a8c;
  font-size: 14px;
}
</style>
